import React, { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, Clock, Zap, AlertCircle, Lock, RefreshCw, Bell, Users, Crown, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  subscribeToPush,
  isSubscribedToPush,
  getNotificationPermission,
  isPushSupported
} from '../services/pushNotifications';

import { BACKEND_URL } from '../config/api';

export default function OnCallToggle({ onStatusChange, onOpenReferrals }) {
  const [status, setStatus] = useState(null);
  const [featureAccess, setFeatureAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [duration, setDuration] = useState(24);
  const [notes, setNotes] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [checkingPush, setCheckingPush] = useState(true);
  const [paying, setPaying] = useState(false);

  const fetchOnCallData = useCallback(async () => {
    try {
      const [statusRes, accessRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/on-call/status`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/on-call/feature-access`, { withCredentials: true }),
      ]);
      setStatus(statusRes.data);
      setFeatureAccess(accessRes.data);
    } catch (error) {
      console.error('Failed to fetch On Call data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOnCallData();
    checkPushStatus();
    const interval = setInterval(fetchOnCallData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchOnCallData]);

  // Handle payment redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('on_call_payment');
    const sessionId = params.get('session_id');
    if (result === 'success' && sessionId) {
      confirmPayment(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (result === 'cancelled') {
      toast.info('On Call payment cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const confirmPayment = async (sessionId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/on-call/confirm-payment?session_id=${sessionId}`,
        {},
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(res.data.message || 'On Call access activated!');
        fetchOnCallData();
      }
    } catch (err) {
      console.error('Payment confirmation failed:', err);
      toast.error('Failed to confirm payment. Please contact support.');
    }
  };

  const checkPushStatus = async () => {
    if (isPushSupported()) {
      const subscribed = await isSubscribedToPush();
      setPushEnabled(subscribed);
    }
    setCheckingPush(false);
  };

  const enablePushNotifications = async () => {
    const result = await subscribeToPush();
    if (result.success) {
      setPushEnabled(true);
      toast.success('Push notifications enabled!');
      return true;
    }
    if (result.error?.includes('denied')) {
      toast.error('Please enable notifications in your browser settings');
    } else {
      toast.error(result.error || 'Failed to enable push notifications');
    }
    return false;
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/on-call/pay`,
        {},
        { withCredentials: true }
      );
      if (res.data.already_paid) {
        toast.success('You already have active On Call access!');
        fetchOnCallData();
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const handleActivate = async () => {
    if (!featureAccess?.can_activate) {
      toast.error(featureAccess?.reason || 'Cannot activate On Call');
      return;
    }
    if (isPushSupported() && !pushEnabled) {
      const permission = getNotificationPermission();
      if (permission !== 'denied') {
        toast.info('Enabling push notifications for urgent requests...');
        await enablePushNotifications();
      }
    }
    setActivating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/on-call/activate`,
        { duration_hours: duration, notes: notes || null, radius_miles: radiusMiles },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowModal(false);
      fetchOnCallData();
      onStatusChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate On Call');
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/on-call/deactivate`, {}, { withCredentials: true });
      toast.success('On Call deactivated');
      fetchOnCallData();
      onStatusChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const handleRenew = async () => {
    setActivating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/on-call/renew`,
        { duration_hours: duration },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchOnCallData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew');
    } finally {
      setActivating(false);
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return '';
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires - now;
    if (diffMs <= 0) return 'Expired';
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${minutes}m remaining` : `${minutes}m remaining`;
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse" data-testid="on-call-loading">
        <div className="h-6 bg-slate-200 rounded w-1/2 mb-2" />
        <div className="h-4 bg-slate-200 rounded w-3/4" />
      </div>
    );
  }

  const isActive = status?.is_active;
  const canActivate = featureAccess?.can_activate;
  const requiresPayment = featureAccess?.requires_payment;
  const accessType = featureAccess?.access_type;
  const settings = featureAccess?.settings || {};
  const referralProgress = featureAccess?.referral_progress;
  const onCallPrice = settings.on_call_price || featureAccess?.on_call_price || 9.99;
  const cooldownActive = featureAccess?.governance?.cooldown_active;

  // ── STATE: ACTIVE ──
  if (isActive) {
    return (
      <>
        <div className="relative overflow-hidden rounded-xl border-2 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300" data-testid="on-call-toggle-card">
          <div className="absolute top-0 right-0">
            <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1">
              <Zap className="h-3 w-3" /> LIVE
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Phone className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 text-lg">On Call - Active</h3>
                <p className="text-sm text-slate-600">You are visible for urgent work requests</p>
              </div>
              {featureAccess?.is_boosted && (
                <span className="inline-flex items-center gap-1 bg-orange-100 border border-orange-300 text-orange-800 px-2 py-0.5 rounded-full text-xs font-bold" data-testid="on-call-priority-boost-badge">
                  <Crown className="h-3 w-3" />
                  PRIORITY
                </span>
              )}
            </div>
            {featureAccess?.is_boosted && featureAccess?.boosted_until && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 mb-3 text-xs text-orange-800" data-testid="on-call-priority-boost-info">
                <span className="font-semibold">Priority Boost active</span> – you appear higher in employer searches until {new Date(featureAccess.boosted_until).toLocaleDateString()}.
              </div>
            )}
            {status?.on_call && (
              <div className="bg-white/60 rounded-lg p-3 mb-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-emerald-700">{formatTimeRemaining(status.on_call.expires_at)}</span>
                </div>
                {status.on_call.notes && <p className="text-sm text-slate-600 italic">"{status.on_call.notes}"</p>}
                <p className="text-xs text-slate-500">Radius: {status.on_call.radius_miles || 25} miles</p>
              </div>
            )}
            {/* Governance warning */}
            {featureAccess?.governance?.consecutive_ignores > 0 && !cooldownActive && (
              <div className="flex items-center gap-2 mb-3 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                <span>Warning: {featureAccess.governance.consecutive_ignores}/3 ignored requests. Respond to avoid being paused.</span>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={handleRenew} disabled={activating}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                data-testid="on-call-renew-btn">
                <RefreshCw className={`h-4 w-4 ${activating ? 'animate-spin' : ''}`} /> Renew
              </button>
              <button onClick={handleDeactivate}
                className="px-4 py-2.5 text-slate-600 font-medium rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors"
                data-testid="on-call-deactivate-btn">End</button>
            </div>
            {/* Metrics */}
            {status?.metrics && status.metrics.total_requests > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Response stats (30 days)</p>
                <div className="flex gap-4 text-sm">
                  <span className="text-slate-700"><strong>{Math.round(status.metrics.response_rate * 100)}%</strong> response</span>
                  <span className="text-slate-700"><strong>{Math.round(status.metrics.acceptance_rate * 100)}%</strong> acceptance</span>
                </div>
              </div>
            )}
            {/* Push notification status */}
            {isPushSupported() && !checkingPush && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                {pushEnabled ? (
                  <div className="flex items-center gap-2 text-xs text-emerald-600"><Bell className="h-3.5 w-3.5" /><span>Push notifications enabled</span></div>
                ) : (
                  <button onClick={enablePushNotifications} className="flex items-center gap-2 text-xs text-amber-600 hover:text-amber-700"><Bell className="h-3.5 w-3.5" /><span>Enable push notifications for urgent requests</span></button>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Activation modal stays rendered for renew flow */}
        <ActivationModal open={showModal} onOpenChange={setShowModal} duration={duration} setDuration={setDuration}
          notes={notes} setNotes={setNotes} radiusMiles={radiusMiles} setRadiusMiles={setRadiusMiles}
          activating={activating} onActivate={handleActivate} maxHours={settings.max_duration_hours || 72} />
      </>
    );
  }

  // ── STATE: GOVERNANCE COOLDOWN ──
  if (cooldownActive) {
    return (
      <div className="relative overflow-hidden rounded-xl border-2 bg-white border-orange-200" data-testid="on-call-toggle-card">
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 rounded-lg bg-orange-100"><AlertCircle className="h-5 w-5 text-orange-600" /></div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 text-lg">On Call Paused</h3>
              <p className="text-sm text-slate-600">You ignored 3 urgent requests. On Call is paused for {Math.round(featureAccess.governance.cooldown_remaining_hours)} hours.</p>
            </div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            Respond to requests quickly to maintain your On Call status. Cooldown ends automatically.
          </div>
        </div>
      </div>
    );
  }

  // ── STATE: LOCKED (requires payment or referral) ──
  if (requiresPayment && accessType === 'none') {
    return (
      <div className="relative overflow-hidden rounded-xl border-2 bg-white border-slate-200" data-testid="on-call-toggle-card">
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 rounded-lg bg-slate-100"><Lock className="h-5 w-5 text-slate-500" /></div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 text-lg" data-testid="on-call-title">{settings.on_call_label_text || 'Go On Call'}</h3>
              <p className="text-sm text-slate-600" data-testid="on-call-description">{settings.on_call_description_text || 'Make yourself available for urgent and emergency work.'}</p>
            </div>
          </div>
          {/* Lock message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="on-call-lock-message">
            <p className="text-sm font-semibold text-amber-800 mb-1">On Call is free</p>
            <p className="text-xs text-amber-700">Be visible for urgent job requests and get priority work.</p>
          </div>
          {/* Benefits */}
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <ul className="text-xs text-slate-700 space-y-1.5">
              <li className="flex items-start gap-2"><Zap className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" /> Receive urgent job requests from employers</li>
              <li className="flex items-start gap-2"><Zap className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" /> Earn more from priority work</li>
              <li className="flex items-start gap-2"><Zap className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" /> Get notified instantly via push notifications</li>
              <li className="flex items-start gap-2"><Zap className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" /> Stand out to employers needing help now</li>
            </ul>
          </div>
          {/* CTA Buttons */}
          <div className="space-y-2">
            <button onClick={handlePay} disabled={paying}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              data-testid="on-call-pay-btn">
              <CreditCard className="h-4 w-4" />
              {paying ? 'Processing...' : `Activate On Call - £${onCallPrice}`}
            </button>
            {settings.on_call_referral_enabled && (
              <button onClick={() => onOpenReferrals?.()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-slate-700 font-medium rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors"
                data-testid="on-call-referral-btn">
                <Users className="h-4 w-4" />
                Earn Priority Boost via Referrals
              </button>
            )}
          </div>
          {/* Referral progress */}
          {settings.on_call_referral_enabled && referralProgress && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-600 mb-2">
                Refer another trade and get a <strong>Priority Boost</strong> when they successfully join. Priority Boost helps your profile appear more prominently in employer searches for a limited time.
              </p>
              {referralProgress.total_qualified > 0 ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium text-emerald-700">{referralProgress.total_qualified} referral{referralProgress.total_qualified !== 1 ? 's' : ''}</span>
                  {referralProgress.unclaimed > 0 && (
                    <span className="text-orange-600 font-semibold">{referralProgress.unclaimed} Priority Boost{referralProgress.unclaimed !== 1 ? 'es' : ''} to claim!</span>
                  )}
                </div>
              ) : (
                <p className="text-[10px] text-slate-400">No qualifying referrals yet. Share your link to get started.</p>
              )}
              <p className="text-[10px] text-slate-400 mt-2 italic">Priority Boost does not guarantee work. Employers still choose who to contact.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── STATE: HAS ACCESS (free for all trades) — ready to activate ──
  return (
    <>
      <div className="relative overflow-hidden rounded-xl border-2 bg-white border-slate-200 hover:border-orange-300 transition-all" data-testid="on-call-toggle-card">
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="p-2 rounded-lg bg-orange-100"><PhoneOff className="h-5 w-5 text-orange-600" /></div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 text-lg">{settings.on_call_label_text || 'Go On Call'}</h3>
              <p className="text-sm text-slate-600">
                On Call is free. Toggle yourself On Call when you are available for urgent work and employers will see you first.
              </p>
            </div>
            {featureAccess?.is_boosted && (
              <span className="inline-flex items-center gap-1 bg-orange-100 border border-orange-300 text-orange-800 px-2 py-0.5 rounded-full text-xs font-bold" data-testid="ready-priority-boost-badge">
                <Crown className="h-3 w-3" />
                PRIORITY
              </span>
            )}
          </div>
          {/* Priority Boost banner */}
          {featureAccess?.is_boosted ? (
            <div className="flex items-center gap-2 mb-3 text-sm text-orange-800 bg-orange-50 border border-orange-200 px-3 py-2 rounded-lg" data-testid="priority-boost-banner">
              <Crown className="h-4 w-4 text-orange-600" />
              <span className="font-medium">Priority Boost Active</span>
              {featureAccess?.boosted_until && (
                <span className="text-xs text-orange-600 ml-auto">until {new Date(featureAccess.boosted_until).toLocaleDateString()}</span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg" data-testid="on-call-free-banner">
              <Zap className="h-4 w-4 text-emerald-600" />
              <span className="font-medium">Free for all tradespeople – no payment required</span>
            </div>
          )}
          {/* Benefits */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 mb-3">
            <p className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2"><Zap className="h-4 w-4" /> Why go On Call?</p>
            <ul className="text-xs text-emerald-700 space-y-1">
              <li>Receive urgent job requests from employers</li>
              <li>Earn more from priority work</li>
              <li>Get notified instantly via push notifications</li>
              <li>Refer trades to earn Priority Boost for higher visibility</li>
            </ul>
          </div>
          {/* Eligibility warning */}
          {status?.eligibility && !status.eligibility.eligible && (
            <div className="flex items-start gap-2 mb-3 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{status.eligibility.reason}</span>
            </div>
          )}
          <button onClick={() => { if (canActivate) { setShowModal(true); } else { toast.error(featureAccess?.reason || 'Cannot activate'); } }}
            disabled={!status?.eligibility?.eligible}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 font-medium rounded-lg transition-colors ${
              status?.eligibility?.eligible ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-slate-200 text-slate-500 cursor-not-allowed'
            }`}
            data-testid="on-call-activate-btn">
            <Phone className="h-4 w-4" />
            Go On Call
          </button>
          {/* Referral CTA for Priority Boost */}
          {settings.on_call_referral_enabled && !featureAccess?.is_boosted && (
            <button onClick={() => onOpenReferrals?.()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 text-orange-700 font-medium rounded-lg border border-orange-300 hover:bg-orange-50 transition-colors text-sm"
              data-testid="on-call-referral-btn">
              <Users className="h-4 w-4" />
              Earn Priority Boost via Referrals
            </button>
          )}
          {/* Referral progress */}
          {settings.on_call_referral_enabled && referralProgress && referralProgress.unclaimed > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-600 mb-1">
                You have <strong className="text-orange-600">{referralProgress.unclaimed} unclaimed referral{referralProgress.unclaimed !== 1 ? 's' : ''}</strong> – worth <strong>{referralProgress.days_available_to_claim} days of Priority Boost</strong>!
              </p>
            </div>
          )}
        </div>
      </div>
      <ActivationModal open={showModal} onOpenChange={setShowModal} duration={duration} setDuration={setDuration}
        notes={notes} setNotes={setNotes} radiusMiles={radiusMiles} setRadiusMiles={setRadiusMiles}
        activating={activating} onActivate={handleActivate} maxHours={settings.max_duration_hours || 72} />
    </>
  );
}

// ── Activation Modal (shared by active-renew and ready-to-activate states) ──
function ActivationModal({ open, onOpenChange, duration, setDuration, notes, setNotes, radiusMiles, setRadiusMiles, activating, onActivate, maxHours }) {
  const durationOptions = [12, 24, 48].filter(h => h <= maxHours);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="h-5 w-5 text-orange-500" /> Activate On Call</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">When On Call, employers can see you're ready for urgent work. You'll be prioritised in search results.</p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Duration</label>
            <div className="grid grid-cols-3 gap-2">
              {durationOptions.map((hours) => (
                <button key={hours} type="button" onClick={() => setDuration(hours)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    duration === hours ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-700 border-slate-300 hover:border-orange-300'
                  }`}>{hours}h</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Travel radius (miles)</label>
            <select value={radiusMiles} onChange={(e) => setRadiusMiles(Number(e.target.value))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              <option value={10}>Up to 10 miles</option>
              <option value={25}>Up to 25 miles</option>
              <option value={50}>Up to 50 miles</option>
              <option value={100}>Up to 100 miles</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Note for employers (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Available for emergency repairs" maxLength={100}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            <strong>Tip:</strong> Respond quickly to urgent requests to maintain your On Call eligibility.
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => onOpenChange(false)} className="flex-1 px-4 py-2 text-slate-700 font-medium border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
          <button onClick={onActivate} disabled={activating}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50"
            data-testid="on-call-confirm-activate-btn">
            {activating ? (<><RefreshCw className="h-4 w-4 animate-spin" /> Activating...</>) : (<><Zap className="h-4 w-4" /> Go On Call</>)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
